import React from 'react';
import { Link, usePage } from '@inertiajs/react';

import Container from '@/components/public/ui/Container';
import Icon from '@/components/public/ui/Icon';
import UtilityBar from '@/components/public/UtilityBar';
import { NavbarLinks } from '@/data/publicNavigation';
import type { NavigationLink } from '@/data/publicNavigation';
import { trackUmamiEvent } from '@/lib/analytics';
import { parishEyebrow, parishShortName, parishLogoSmall, useSite } from '@/hooks/useSite';

const MobileMenuId = 'mobile-nav';

const isLinkActive = (link: NavigationLink, pathname: string): boolean => {
  if (link.href && pathname === link.href) return true;
  if (link.children) return link.children.some((child) => child.href === pathname);
  return false;
};

// Ink masthead: light links with a gold-leaf underline marking the active page.
// Border colour lives on idle/active (not the base) so the two never conflict.
const linkBase = 'border-b-2 px-3 py-2 text-sm transition-colors duration-200';
const linkIdle = 'border-transparent text-white/70 hover:text-white font-medium';
const linkActive = 'border-gold-leaf text-white font-semibold';

const Logo = () => {
  const site = useSite();
  const shortName = parishShortName(site);
  const sub = ['Junta de Freguesia', site.district].filter(Boolean).join(' · ');

  return (
    <Link
      href="/"
      aria-label={`Página inicial de ${shortName}`}
      className="flex items-center gap-3"
    >
      <img
        src={parishLogoSmall(site)}
        alt={`Brasão de ${shortName}`}
        width="84"
        height="56"
        className="h-12 w-auto flex-shrink-0 md:h-14"
      />
      {/* Compact name (mobile) */}
      <span className="font-headline text-sm font-semibold text-white sm:hidden">{shortName}</span>
      {/* Full wordmark (sm+) */}
      <span className="hidden h-9 w-px flex-shrink-0 bg-white/15 sm:block" />
      <span className="hidden flex-col leading-tight sm:flex">
        <span className="text-gold-leaf text-[10px] font-bold tracking-[0.16em] uppercase">
          {parishEyebrow(site)}
        </span>
        <span className="font-headline text-[15px] font-semibold text-white">{shortName}</span>
        <span className="text-[11px] font-medium text-white/55">{sub}</span>
      </span>
    </Link>
  );
};

type DesktopNavigationLinkProps = {
  pathname: string;
  openDropdown: string | null;
  setOpenDropdown: (value: string | null) => void;
  links: NavigationLink[];
};

const DesktopNavigationLinks = (props: DesktopNavigationLinkProps) => {
  return (
    <div className="ml-auto hidden items-center gap-1 md:flex">
      {props.links.map((link) => {
        const isActive = isLinkActive(link, props.pathname);

        if (link.children) {
          const isOpen = props.openDropdown === link.label;
          return (
            <div key={link.label} className="relative" data-nav-dropdown>
              <button
                type="button"
                onClick={() => props.setOpenDropdown(isOpen ? null : link.label)}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                className={`${linkBase} inline-flex items-center gap-1 ${isActive ? linkActive : linkIdle}`}
              >
                {link.label}
                <Icon name="expand_more" size="sm" />
              </button>
              {isOpen && (
                <div
                  role="menu"
                  className="bg-on-surface absolute top-full left-1/2 mt-2 w-[320px] -translate-x-1/2 overflow-hidden rounded-lg border border-white/10 p-2 shadow-2xl"
                >
                  {link.children.map((child) => {
                    const childActive = props.pathname === child.href;
                    return (
                      <Link
                        key={child.href}
                        href={child.href}
                        role="menuitem"
                        aria-current={childActive ? 'page' : undefined}
                        onClick={() => {
                          trackUmamiEvent('navigation_click', {
                            source: 'desktop_dropdown',
                            label: child.label,
                            target: child.href,
                          });
                          props.setOpenDropdown(null);
                        }}
                        className={`block rounded-md px-3 py-2.5 transition-colors duration-200 ${
                          childActive
                            ? 'bg-white/10 text-white'
                            : 'text-white/85 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        <div className="text-sm font-semibold">{child.label}</div>
                        {child.description && (
                          <div className="mt-0.5 text-[11px] leading-snug text-white/55">
                            {child.description}
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }

        return (
          <Link
            key={link.href + link.label}
            href={link.href}
            aria-current={isActive ? 'page' : undefined}
            onClick={() =>
              trackUmamiEvent('navigation_click', {
                source: 'desktop_nav',
                label: link.label,
                target: link.href,
              })
            }
            className={`${linkBase} ${isActive ? linkActive : linkIdle}`}
          >
            {link.label}
          </Link>
        );
      })}
    </div>
  );
};

type MobileHamburgerProps = {
  setMobileOpen: (value: boolean) => void;
  mobileOpen: boolean;
};

const MobileHamburger = (props: MobileHamburgerProps) => {
  return (
    <button
      type="button"
      className="ml-auto p-1 text-white/80 transition-colors hover:text-white md:hidden"
      onClick={() => props.setMobileOpen(!props.mobileOpen)}
      aria-label={props.mobileOpen ? 'Fechar menu' : 'Abrir menu'}
      aria-expanded={props.mobileOpen}
      aria-controls={MobileMenuId}
    >
      <Icon name={props.mobileOpen ? 'close' : 'menu'} size="md" />
    </button>
  );
};

type MobileMenuProps = {
  pathname: string;
  setMobileOpen: (value: boolean) => void;
  links: NavigationLink[];
};

const MobileMenu = (props: MobileMenuProps) => {
  return (
    <div
      id={MobileMenuId}
      className="section-ink azulejo-on-ink absolute inset-x-0 top-full border-t border-white/10 shadow-xl md:hidden"
    >
      <Container className="flex flex-col gap-1 py-4">
        {props.links.map((link) => {
          if (link.children) {
            return (
              <div key={link.label} className="flex flex-col gap-1 pt-2">
                <div className="text-gold-leaf px-3 pb-1 text-[11px] font-bold tracking-widest uppercase">
                  {link.label}
                </div>
                {link.children.map((child) => {
                  const childActive = props.pathname === child.href;
                  return (
                    <Link
                      key={child.href}
                      href={child.href}
                      aria-current={childActive ? 'page' : undefined}
                      onClick={() => {
                        trackUmamiEvent('navigation_click', {
                          source: 'mobile_nav',
                          label: child.label,
                          target: child.href,
                        });
                        props.setMobileOpen(false);
                      }}
                      className={`rounded-md px-3 py-2 text-sm transition-colors ${
                        childActive
                          ? 'bg-white/10 font-semibold text-white'
                          : 'font-medium text-white/75 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      {child.label}
                    </Link>
                  );
                })}
              </div>
            );
          }

          const isActive = props.pathname === link.href;
          return (
            <Link
              key={link.href + link.label}
              href={link.href}
              aria-current={isActive ? 'page' : undefined}
              onClick={() => {
                trackUmamiEvent('navigation_click', {
                  source: 'mobile_nav',
                  label: link.label,
                  target: link.href,
                });
                props.setMobileOpen(false);
              }}
              className={`rounded-md px-3 py-2 text-sm transition-colors ${
                isActive
                  ? 'bg-white/10 font-semibold text-white'
                  : 'font-medium text-white/75 hover:bg-white/10 hover:text-white'
              }`}
            >
              {link.label}
            </Link>
          );
        })}
      </Container>
    </div>
  );
};

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [openDropdown, setOpenDropdown] = React.useState<string | null>(null);
  const { url } = usePage();
  const pathname = url.startsWith('http') ? new URL(url).pathname : url.split('?')[0];

  // Drop the "Serviços" entry when the parish offers no services.
  const site = useSite();
  const links =
    site.services.length > 0 ? NavbarLinks : NavbarLinks.filter((l) => l.href !== '/services');

  // Close on Escape.
  React.useEffect(() => {
    if (!mobileOpen && !openDropdown) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
        setOpenDropdown(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen, openDropdown]);

  // Close dropdown on outside click.
  React.useEffect(() => {
    if (!openDropdown) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-nav-dropdown]')) setOpenDropdown(null);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [openDropdown]);

  return (
    <header>
      <UtilityBar />
      <div className="sticky top-0 z-50">
        <div className="section-ink azulejo-on-ink border-t border-white/10">
          <Container className="flex items-center gap-4 py-2.5">
            <Logo />
            <nav aria-label="Navegação principal" className="contents">
              <DesktopNavigationLinks
                pathname={pathname}
                openDropdown={openDropdown}
                setOpenDropdown={setOpenDropdown}
                links={links}
              />
              <MobileHamburger setMobileOpen={setMobileOpen} mobileOpen={mobileOpen} />
            </nav>
          </Container>
        </div>
        <div className="gov-rule" />
        {mobileOpen && (
          <MobileMenu pathname={pathname} setMobileOpen={setMobileOpen} links={links} />
        )}
      </div>
    </header>
  );
};

export default Navbar;
